import { useRef, useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { BulkReleaseHandle, BulkScope } from '@/hooks/workbench/bulkRelease';

/**
 * Der Knopf "Freigeben" aus Spec 9.1 samt seiner Rueckfrage (9.3).
 *
 * Die Komponente ist bewusst rein: sie bekommt jede Zahl und jede Handlung als
 * Prop (`BulkReleaseHandle`) und stellt selbst keine Abfrage. Der Zustand
 * entsteht im Reviewer, der Knopf steht in der Kopfzeile der Seite - eine
 * eigene Abfrage hier haette dieselbe Zahl ein zweites Mal berechnet, und zwei
 * Rechenwege driften irgendwann auseinander. Genau daran ist der abgeloeste
 * Filterleisten-Knopf gescheitert.
 */
export type WorkbenchReleaseMenuProps = BulkReleaseHandle;

export function WorkbenchReleaseMenu({
  summary,
  summaryFailed,
  retrySummary,
  isBusy,
  hasActiveSearch,
  approveScope,
  showWarningsOnly,
  enableSelection,
}: WorkbenchReleaseMenuProps) {
  const { t } = useTranslation('workbench');

  /**
   * Die Unterzeilen stehen als BESCHREIBUNG am Eintrag, nicht in seinem Namen.
   *
   * Als Teil des Inhalts wandern sie in den zugaenglichen Namen und machen aus
   * "2.914 sichere freigeben" einen Satz von 60 Zeichen - jedes Vorlesen und
   * jede Namenssuche traegt dann die ganze Regel mit. Jeder Eintrag traegt
   * deshalb ein `aria-label` mit GENAU seiner sichtbaren Hauptzeile, und die
   * Unterzeile haengt per `aria-describedby` daran: angesagt wird beides,
   * benannt nur das eine. Nichts geht verloren.
   */
  const hinweisId = useId();
  /** `null` = Dialog zu. Der Wert traegt zugleich, WELCHE Menge bestaetigt wird. */
  const [confirmScope, setConfirmScope] = useState<BulkScope | null>(null);
  /**
   * Ein eigener State fuer den rechtlichen Hinweis, NICHT `confirmScope`.
   *
   * `confirmScope` traegt in seinem Wert die zu bestaetigende MENGE - der
   * Hinweis bestaetigt nichts und hat keine. Ihn ueber denselben State zu
   * fahren (z.B. ein viertes `BulkScope`-Mitglied "info") wuerde den
   * Bestaetigungsdialog zwingen, fuer einen Fall ohne Menge Zahlen zu
   * unterdruecken, die er sonst immer zeigt.
   */
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * Zweiter Riegel gegen den Doppelklick. `isSubmitting` allein reicht nicht:
   * zwei Klicks im selben Tick sehen beide noch den alten State, weil React
   * erst danach neu rendert. Der Ref ist sofort gesetzt.
   */
  const submittingRef = useRef(false);
  /**
   * Radix gibt beim Schliessen des Menues den Fokus an den Trigger zurueck.
   * Oeffnet derselbe Klick einen Dialog, kaempfen beide um den Fokus und der
   * Dialog verliert. Deshalb merken wir uns, dass ein Dialog kommt, und
   * unterdruecken die Fokus-Rueckgabe genau dann.
   */
  const openingDialogRef = useRef(false);

  /**
   * Nichts anbieten, solange die Zahlen fehlen. Ein Knopf ohne Zahl waere
   * entweder stumm oder muesste eine erfinden.
   *
   * Der Fehlerfall steht bewusst VOR dieser Pruefung: wenn die Abfrage rot
   * ist, wird der Knopf gezeigt und benennt den Fehler. Ein stiller Rueckfall
   * auf "keine Kandidaten" waere von "es gibt wirklich keine" nicht
   * unterscheidbar (K5).
   */
  if (!summaryFailed && (!summary || summary.openCount === 0)) return null;

  const openConfirm = (scope: BulkScope) => {
    openingDialogRef.current = true;
    setConfirmScope(scope);
  };

  const openDisclaimer = () => {
    openingDialogRef.current = true;
    setDisclaimerOpen(true);
  };

  /**
   * V3-Befund (uebernommen aus WorkbenchRail, hier zweimal gebraucht): Radix
   * gibt den Fokus an das zuvor fokussierte Element zurueck, und das kann
   * durch die bestaetigte Handlung verschwunden sein. Dann faellt der Fokus
   * auf <body> und Tab beginnt wieder ganz oben.
   */
  const restoreTabFocusFallback = () => {
    // Microtask statt setTimeout: ein Timer kann den Unmount (und in Tests
    // den Abbau der DOM-Umgebung) ueberleben.
    queueMicrotask(() => {
      if (typeof document === 'undefined') return;
      if (document.activeElement === document.body) {
        document.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus();
      }
    });
  };

  /**
   * Baut die Aufschluesselung aus zaehlbaren TEILEN statt aus einem Satz mit
   * zwei Platzhaltern.
   *
   * Der eine Satz konnte beides nicht: er druckte eine Null, sobald nur eine
   * der beiden Mengen leer war ("auch die 0 unter 40 Punkten und die 5 mit
   * Hinweisen", live gesehen), und er stand bei einer 1 im Plural ("1
   * Produkte unter 40 Punkten"), weil i18next nur EINEN `count` pluralisieren
   * kann. Zwei Zahlen in einem Satz sind mit `count` nicht zu beugen.
   *
   * Aus Teilen gebaut ist jeder Teil fuer sich pluralisiert, und leere Mengen
   * fallen einfach weg. Bleibt gar nichts uebrig, ist die Menge sauber und
   * bekommt ihren eigenen Satz - dafuer gibt der Helfer null zurueck.
   */
  const aufschluesselung = (rahmenEins: string, rahmenZwei: string): string | null => {
    if (summary === null) return null;
    const teile: string[] = [];
    if (summary.lowScoreCount > 0) {
      teile.push(t('release.partLowScore', { count: summary.lowScoreCount }));
    }
    if (summary.warningCount > 0) {
      teile.push(t('release.partWarnings', { count: summary.warningCount }));
    }
    if (teile.length === 0) return null;
    if (teile.length === 1) return t(rahmenEins, { a: teile[0] });
    return t(rahmenZwei, { a: teile[0], b: teile[1] });
  };

  const confirmCount =
    summary === null ? 0 : confirmScope === 'safe' ? summary.safeCount : summary.openCount;

  /**
   * Ist an der Menge nichts Kritisches?
   *
   * Nur dann darf die ausgeschriebene Fassung stehen ("kein Produkt unter 40
   * Punkten und keines mit Hinweisen"). Die Zahlenfassung haette hier "0
   * Produkte ... und 0 mit Hinweisen" gesagt - dieselbe Aussage, nur muss man
   * dafuer erst zwei Nullen lesen. Unbewertete Produkte zaehlen bewusst NICHT
   * hinein: sie stehen in einem eigenen Satz, weil "ohne Punktzahl"
   * unbewertet heisst und nicht schlecht.
   */
  const istSauber =
    summary !== null && summary.lowScoreCount === 0 && summary.warningCount === 0;

  const handleConfirm = async () => {
    if (submittingRef.current || confirmScope === null) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      /**
       * `approveScope` loest mit einer ZAHL auf und wirft nie. 0 heisst "es ist
       * nichts passiert", und auch das ist ein gueltiger Ausgang: der Aufrufer
       * meldet ihn. Hier wird deshalb kein Erfolg behauptet, nicht einmal
       * implizit ueber einen Toast.
       */
      await approveScope(confirmScope);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setConfirmScope(null);
    }
  };

  const actionsBlocked = isBusy || isSubmitting;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={isBusy}
            /* T1/W1: der Trigger ist kein Primaerknopf mehr. Die gefuellte
               Erfolgsflaeche (K8) traegt seitdem allein der Freigeben-Knopf
               der Kartenleiste (WorkbenchActionBar) - zwei gefuellte
               Erfolgsflaechen auf derselben Seite waeren zwei Primaerknoepfe. */
            className="gap-1.5"
            data-testid="workbench-release-trigger"
          >
            {t('release.trigger')}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72"
          onCloseAutoFocus={(event) => {
            if (openingDialogRef.current) {
              openingDialogRef.current = false;
              event.preventDefault();
            }
          }}
        >
          {summaryFailed || summary === null ? (
            <>
              {/* K5: der Fehler bekommt Worte. Eine 0 an dieser Stelle waere
                  eine Behauptung ueber den Feed, die wir gar nicht kennen. */}
              <DropdownMenuLabel className="font-normal text-destructive-ink">
                {t('release.summaryFailed')}
              </DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => retrySummary()}>
                {t('release.retry')}
              </DropdownMenuItem>
            </>
          ) : (
            <>
              {/* Die Empfehlung haengt an der Zahl, nicht am Eintrag: auf zwei
                  der vier gemessenen Feeds ist safeCount 0, ein fest
                  verdrahtetes "(empfohlen)" empfaehle dort die leere Menge. */}
              <DropdownMenuItem
                disabled={summary.safeCount === 0}
                onSelect={() => openConfirm('safe')}
                className="flex-col items-start gap-0.5"
                aria-label={
                  summary.safeCount === 0
                    ? t('release.safeNone')
                    : `${t('release.safe', { count: summary.safeCount })} ${t('release.recommended')}`
                }
                aria-describedby={`${hinweisId}-safe`}
              >
                <span>
                  {summary.safeCount === 0
                    ? t('release.safeNone')
                    : `${t('release.safe', { count: summary.safeCount })} ${t('release.recommended')}`}
                </span>
                {/* Die Regel hinter "sicher" stand nirgends in der Oberflaeche.
                    Wer nicht weiss, was der Knopf auswaehlt, kann eine
                    Sammelfreigabe nicht verantworten. */}
                <span id={`${hinweisId}-safe`} className="text-xs font-normal text-muted-foreground">
                  {t('release.hintSafe')}
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem
                disabled={summary.warningCount === 0}
                onSelect={() => showWarningsOnly()}
                className="flex-col items-start gap-0.5"
                aria-label={t('release.warnings', { count: summary.warningCount })}
                aria-describedby={`${hinweisId}-warn`}
              >
                {/* Setzt NUR den Filter. Dieser Eintrag gibt nichts frei, und
                    genau deshalb hat er keine Rueckfrage. */}
                <span>{t('release.warnings', { count: summary.warningCount })}</span>
                <span id={`${hinweisId}-warn`} className="text-xs font-normal text-muted-foreground">
                  {t('release.hintWarnings')}
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onSelect={() => openConfirm('all')}
                className="flex-col items-start gap-0.5"
                aria-label={t('release.all', { count: summary.openCount })}
                aria-describedby={`${hinweisId}-all`}
              >
                <span>{t('release.all', { count: summary.openCount })}</span>
                <span id={`${hinweisId}-all`} className="text-xs font-normal text-muted-foreground">
                  {aufschluesselung('release.hintAllOne', 'release.hintAllTwo') ??
                    t('release.hintAllClean')}
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onSelect={() => enableSelection()}
                className="flex-col items-start gap-0.5"
                aria-label={t('release.selection')}
                aria-describedby={`${hinweisId}-sel`}
              >
                <span>{t('release.selection')}</span>
                <span id={`${hinweisId}-sel`} className="text-xs font-normal text-muted-foreground">
                  {t('release.hintSelection')}
                </span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              {/* Die haeufigste Fehlannahme an dieser Stelle: "freigegeben" wird
                  fuer "bei Google" gehalten. Sie steht deshalb im Menue UND in
                  der Rueckfrage, nicht nur an einer der beiden Stellen. */}
              <DropdownMenuLabel className="whitespace-normal text-xs font-normal text-muted-foreground">
                {t('release.footer')}
              </DropdownMenuLabel>
            </>
          )}

          {/* B1-Fix: Fuenfter Eintrag (T1), der rechtliche Hinweis - bewusst
              AUSSERHALB der Ternary oben. Vorher hing er im Erfolgszweig und
              war deshalb genau dann unerreichbar, wenn die Sammelfreigabe-
              Zahlen noch laden oder ihre Abfrage fehlschlaegt (nur Fehlertext
              + "Erneut versuchen" waren sichtbar). Vor dem C2-Umbau stand der
              Rechtstext als Info-Popover an der Kartenleiste und war IMMER
              da - sein Inhalt (der Haftungssatz) haengt an keiner Zahl. */}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openDisclaimer}>
            {t('release.disclaimer')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={confirmScope !== null}
        onOpenChange={(open) => {
          if (!open && !submittingRef.current) setConfirmScope(null);
        }}
      >
        <AlertDialogContent onCloseAutoFocus={restoreTabFocusFallback}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('release.confirmTitle', { count: confirmCount })}</AlertDialogTitle>
            {/* Die Beschreibung ist ein <p>. Die einzelnen Saetze sind deshalb
                <span className="block"> und keine <p> - verschachtelte
                Absaetze sind ungueltiges HTML. */}
            <AlertDialogDescription>
              <span className="block">
                {t(hasActiveSearch ? 'release.confirmScopeSearch' : 'release.confirmScopeFeed')}
              </span>

              {/* 1. Was drin ist, in Zahlen.

                  Sind beide Zahlen null, greift die ausgeschriebene Fassung
                  ("kein Produkt unter 40 Punkten und keines mit Hinweisen")
                  statt "0 Produkte ... und 0 mit Hinweisen". Gleiche Aussage,
                  und der Nutzer muss keine Null lesen, um zu verstehen, dass
                  nichts Kritisches dabei ist. */}
              {confirmScope === 'all' && summary !== null && !istSauber ? (
                <>
                  <span className="mt-2 block">
                    {aufschluesselung(
                      'release.confirmBreakdownOne',
                      'release.confirmBreakdownTwo',
                    )}
                  </span>
                  {/* Der Folgesatz aus Mockup C2Dialog. Ohne ihn sind die
                      beiden Zahlen eine Statistik; mit ihm sind sie die
                      Folge, der man gerade zustimmt. Bewusst NICHT "die
                      siehst du dir danach nicht mehr an" - freigegebene
                      Produkte stehen weiterhin im Reiter "Freigegeben" und
                      lassen sich von dort zurueckholen. Was wirklich
                      entfaellt, ist die EINZELPRUEFUNG. */}
                  <span className="mt-2 block font-medium text-foreground">
                    {t('release.confirmBreakdownConsequence')}
                  </span>
                  {summary.unscoredCount > 0 && (
                    /* Getrennt genannt, nicht in die 40-Punkte-Zahl gefaltet:
                       ohne Punktzahl heisst unbewertet, nicht schlecht. Nur im
                       Umfang "alle" - die sichere Menge verlangt mindestens 70
                       Punkte, ein unbewertetes Produkt kann dort gar nicht
                       drin sein, und die feed-weite Zahl waere dort schlicht
                       eine Aussage ueber eine andere Menge. */
                    <span className="mt-2 block">
                      {t('release.confirmUnscored', { count: summary.unscoredCount })}
                    </span>
                  )}
                </>
              ) : (
                <span className="mt-2 block">{t('release.confirmSafeClean')}</span>
              )}

              {/* Der Hinweis auf unbewertete Produkte gilt auch im sauberen
                  Fall - "keines unter 40 Punkten" ist keine Aussage ueber
                  Produkte, die gar keine Punktzahl haben. Vorher hing er
                  ausschliesslich am Zahlen-Zweig und verschwand damit genau
                  dann, wenn er als einziger Vorbehalt uebrig blieb. */}
              {confirmScope === 'all' && summary !== null && istSauber && summary.unscoredCount > 0 && (
                <span className="mt-2 block">
                  {t('release.confirmUnscored', { count: summary.unscoredCount })}
                </span>
              )}

              {/* 2. Was NICHT passiert. */}
              <span className="mt-2 block">{t('release.confirmNotGoogle')}</span>

              {/* 3. Der Haftungssatz, ausgeschrieben. Bewusst der bestehende
                  Schluessel: ein zweiter Rechtstext wuerde gegen den ersten
                  veralten. */}
              <span className="mt-2 block">{t('disclaimer')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionsBlocked}>
              {t('release.confirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={actionsBlocked}
              onClick={(event) => {
                /* Radix schliesst den Dialog beim Klick auf die Aktion von
                   selbst. Das wuerde `disabled` wirkungslos machen (ein
                   geschlossener Dialog kann nicht doppelt bestaetigt werden,
                   der laufende Schreibvorgang aber schon), und der Ladezustand
                   waere nie sichtbar. Wir schliessen im finally selbst. */
                event.preventDefault();
                void handleConfirm();
              }}
            >
              {isSubmitting ? t('release.confirmActionLoading') : t('release.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fuenfter Eintrag (T1): der Rechtstext als eigener, kleiner Dialog.
          Kein Bestaetigen, keine Menge - nur Text und ein Schliessen-Knopf. */}
      <AlertDialog open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
        <AlertDialogContent onCloseAutoFocus={restoreTabFocusFallback}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('release.disclaimerTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('disclaimer')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDisclaimerOpen(false)}>
              {t('release.disclaimerClose')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
